import PropTypes from 'prop-types';
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';
import {
  Card, CardBody, Col, Row,
} from 'reactstrap';

import Loading from 'src/components/Loading';
import Breadcrumbs from 'src/enosikit/components/Breadcrumbs';
import Heading from 'src/enosikit/components/Heading';
import FlashesStore from 'src/stores/FlashesStore';
import { TRADE_DIRECTION_BUY, TRADE_DIRECTION_SELL } from 'src/util/constants';

import isUserTypeCustomer from 'src/auth/helper';
import { buildTradeRuleProposalCardList, getTradeRules } from './helpers';

const TradeRuleCustomerProposalsFragment = graphql`
  fragment TradeRuleCustomerProposals_viewer on Viewer {
    id
    viewerUser { id type }
    meters(first: 500) {
      edges {
        node {
          id
          identifier
          title
          tradePointId
          active { start finish }
          property {
            id
            title
            active { start finish }
          }
        }
      }
    }
    proposals: rules(first:500, type: TRADE_TYPE_NOMINATED, state: TRADE_RULE_STATE_PROPOSED) {
      edges {
        node {
          id
          clauses {
            edges {
              node {
                price
              }
            }
          }
          tradeType
          state
          buyer {
            user { id email givenName familyName }
            tradePoint { id type }
          }
          seller {
            user { id email givenName familyName }
            tradePoint { id type }
          }
          proposedAt
          proposedBy { id email givenName familyName }
        }
      }
    }
  }
`;

/**
 * TradeRuleCustomerProposals functional component displays customer trade rule proposals.
 * @param {object} props - Component props
 * @param {object} props.viewer - Viewer data from GraphQL fragment
 * @param {object} props.router - React Router object providing navigation capabilities
 * @returns {React.ReactElement} The TradeRuleCustomerProposals component
 */
function TradeRuleCustomerProposals({ viewer, router }) {
  if (!viewer || !router) {
    return <Loading />;
  }

  const intl = useIntl();

  const viewerData = useFragment(TradeRuleCustomerProposalsFragment, viewer);

  const { proposals, viewerUser } = viewerData || {};
  const { id } = viewerUser || {};
  const { type: userType } = viewerUser || {};

  if (!isUserTypeCustomer(userType)) {
    const errorMsg = intl.formatMessage({ id: 'trade_rule.trade_rule_customer_proposals.only_customer_users_can_view_proposals.message', defaultMessage: 'Only customers can view their proposals.' });
    FlashesStore.flash(FlashesStore.ERROR, errorMsg);
    router.replace('/properties');
  }

  const tradeRules = getTradeRules(id, proposals);
  const {
    buy = [], counterPartyBuy = [], counterPartySell = [], sell = [],
  } = tradeRules || {};

  const totalBuys = buy.length + counterPartySell.length;
  const totalSells = sell.length + counterPartyBuy.length;

  // i18n strings
  const pageTitle = intl.formatMessage({ id: 'trade_rule.trade_rule_customer_proposals.page.title', defaultMessage: 'Enosi - Manage my proposals' });
  const tradeRulesLabel = intl.formatMessage({ id: 'trade_rule.trade_rule_customer_proposals.breadcrumbs.trade_rules.label', defaultMessage: 'Trade Rules' });
  const proposalsLabel = intl.formatMessage({ id: 'trade_rule.trade_rule_customer_proposals.breadcrumbs.proposals.label', defaultMessage: 'Proposals' });
  const headingTradeRulesLabel = intl.formatMessage({ id: 'trade_rule.trade_rule_customer_proposals.heading.alias.trade_rules.label', defaultMessage: 'Trade Rules' });
  const headingProposalsLabel = intl.formatMessage({ id: 'trade_rule.trade_rule_customer_proposals.heading.context.proposals.label', defaultMessage: 'Proposals' });
  const proposedTradesTitle = intl.formatMessage({ id: 'trade_rule.trade_rule_customer_proposals.proposed_trades.title', defaultMessage: 'Proposed Trades' });
  const sellingLabel = intl.formatMessage({ id: 'trade_rule.trade_rule_customer_proposals.selling.label', defaultMessage: 'Selling' });
  const buyingLabel = intl.formatMessage({ id: 'trade_rule.trade_rule_customer_proposals.buying.label', defaultMessage: 'Buying' });

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{pageTitle}</title>
      </Helmet>

      <Breadcrumbs
        breadcrumbs={[
          { name: tradeRulesLabel, path: '/trade-rules' },
          { name: proposalsLabel },
        ]}
      />
      <Heading
        title={headingTradeRulesLabel}
        context={headingProposalsLabel}
      />

      <Card className="mt-4 mb-4 trade-proposals">
        <CardBody>
          <h2>{proposedTradesTitle}</h2>
          <Row>
            <Col xs="12" sm="12" md="6">
              <h3>{buyingLabel}</h3>
              <p data-testid="buying">
                {intl.formatMessage(
                  {
                    id: 'trade_rule.trade_rule_customer_proposals.buying.text',
                    defaultMessage: 'You have {totalBuys} proposed trade rules to buy energy.',
                  },
                  {
                    totalBuys: <strong>{totalBuys}</strong>,
                  },
                )}
              </p>
              {buildTradeRuleProposalCardList(intl, buy, viewerData, TRADE_DIRECTION_BUY)}
              {buildTradeRuleProposalCardList(
                intl,
                counterPartySell,
                viewerData,
                TRADE_DIRECTION_SELL,
              )}
            </Col>

            <Col xs="12" sm="12" md="6">
              <h3>{sellingLabel}</h3>
              <p data-testid="selling">
                {intl.formatMessage(
                  {
                    id: 'trade_rule.trade_rule_customer_proposals.selling.text',
                    defaultMessage: 'You have {totalSells} proposed trade rules to sell energy.',
                  },
                  {
                    totalSells: <strong>{totalSells}</strong>,
                  },
                )}
              </p>
              {buildTradeRuleProposalCardList(intl, sell, viewerData, TRADE_DIRECTION_SELL)}
              {buildTradeRuleProposalCardList(
                intl,
                counterPartyBuy,
                viewerData,
                TRADE_DIRECTION_BUY,
              )}
            </Col>

          </Row>
        </CardBody>
      </Card>
    </>
  );
}

TradeRuleCustomerProposals.propTypes = {
  viewer: PropTypes.shape({
    viewerUser: PropTypes.shape({
      id: PropTypes.string,
      type: PropTypes.string,
    }),
    meters: PropTypes.shape({
      edges: PropTypes.arrayOf(
        PropTypes.shape({
          node: PropTypes.shape({
            id: PropTypes.string,
            identifier: PropTypes.string,
            tradePointId: PropTypes.string,
            active: PropTypes.shape({
              start: PropTypes.number,
              finish: PropTypes.number,
            }),
          }),
        }),
      ),
    }),
    proposals: PropTypes.shape({
      edges: PropTypes.arrayOf(
        PropTypes.shape({
          node: PropTypes.shape({
            id: PropTypes.string,
          }),
        }),
      ),
    }),
  }).isRequired,
  router: PropTypes.shape({
    replace: PropTypes.func,
  }).isRequired,
};

export default TradeRuleCustomerProposals;
